"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/shared/button";
import { Input } from "@/components/shared/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  AuthStatus,
  useAuthContext,
  useAuthLoginOrRegister,
} from "@/providers/auth-provider";
import { LoadingSpinner } from "@/components/shared/loading-spinner";

const formSchema = z.object({
  email: z.email({
    error: "Please enter a valid email address.",
  }),
});

export const EmailForm = () => {
  const authContext = useAuthContext();
  const loginOrRegister = useAuthLoginOrRegister();
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState<string>("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: authContext.email || "",
    },
    mode: "onChange",
  });

  const isSubmitting =
    authContext.login.loading &&
    authContext.status === AuthStatus.Authenticating;
  const hasError = authContext.login.error;

  useEffect(() => {
    if (authContext.email && authContext.email !== form.getValues("email")) {
      form.setValue("email", authContext.email);
    }
  }, [authContext.email, form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setLastSubmittedEmail(values.email);
    await loginOrRegister.authLoginOrRegister({ email: values.email });
  }

  return (
    <Card className="max-w-md mx-auto">
      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Enter your email"
                        type="email"
                        disabled={isSubmitting}
                        className={`pr-10 ${
                          form.formState.errors.email
                            ? "border-red-300 focus:ring-red-500"
                            : ""
                        }`}
                        {...field}
                      />
                    </div>
                  </FormControl>

                  <FormMessage />

                  {/* Show auth error */}
                  {hasError && (
                    <div className="space-y-1">
                      <p className="text-sm text-red-600">
                        Authentication failed. Please try again.
                      </p>
                      {lastSubmittedEmail &&
                        lastSubmittedEmail !== field.value && (
                          <p className="text-xs text-gray-500">
                            Try with a different email address
                          </p>
                        )}
                    </div>
                  )}
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <Button
                type="submit"
                variant={form.formState.isValid ? "active" : "muted"}
                className="w-full"
                disabled={isSubmitting || !form.formState.isValid}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" />
                    Sending code...
                  </div>
                ) : form.formState.isValid ? (
                  "Continue"
                ) : (
                  "Enter email to continue"
                )}
              </Button>

              {/* Help text */}
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  We'll send you a 6-digit verification code
                </p>
              </div>

              {/* Terms */}
              <div className="text-center text-xxs text-[rgba(33,33,33,0.5)]">
                By continuing, you acknowledge At Last's{" "}
                <a
                  href="https://www.atlast.co/legal/customer-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-700"
                >
                  Terms of Service
                </a>{" "}
                &{" "}
                <a
                  href="https://www.atlast.co/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-700"
                >
                  Privacy Policy
                </a>
                .
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
